import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createCompanyFileRequest,
  getCompanyFiles,
  removeCompanyFile,
  updateCompanyFileRequest,
} from "../../../../services/api/landing/companyFiles/companyFilesApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: { en: "File created successfully!", ar: "تم إنشاء الملف بنجاح!" },
  addError: { en: "Failed to create file.", ar: "فشل في إنشاء الملف." },
  update: { en: "File updated successfully!", ar: "تم تحديث الملف بنجاح!" },
  updateError: { en: "Failed to update file.", ar: "فشل في تحديث الملف." },
  delete: { en: "File deleted successfully!", ar: "تم حذف الملف بنجاح!" },
  deleteError: { en: "Failed to delete file.", ar: "فشل في حذف الملف." },
};

export const fetchCompanyFiles = createAsyncThunk(
  "companyFiles/fetchCompanyFiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCompanyFiles();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch company files");
    }
  },
);

export const createCompanyFile = createAsyncThunk(
  "companyFiles/createCompanyFile",
  async (fileData, { rejectWithValue }) => {
    try {
      const response = await createCompanyFileRequest(fileData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create company file");
    }
  },
);

export const updateCompanyFile = createAsyncThunk(
  "companyFiles/updateCompanyFile",
  async ({ fileId, fileData }, { rejectWithValue }) => {
    try {
      const response = await updateCompanyFileRequest(fileId, fileData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update company file");
    }
  },
);

export const deleteCompanyFile = createAsyncThunk(
  "companyFiles/deleteCompanyFile",
  async (fileId, { rejectWithValue }) => {
    try {
      await removeCompanyFile(fileId);
      return fileId;
    } catch (error) {
      return rejectWithValue("Failed to delete company file");
    }
  },
);

const companyFilesSlice = createSlice({
  name: "companyFiles",
  initialState: {
    companyFileData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.companyFileData = action.payload;
      })
      .addCase(fetchCompanyFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCompanyFile.fulfilled, (state, action) => {
        state.companyFileData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createCompanyFile.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateCompanyFile.fulfilled, (state, action) => {
        const index = state.companyFileData.findIndex(
          (file) => file.id === action.payload.id,
        );
        if (index !== -1) {
          state.companyFileData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateCompanyFile.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteCompanyFile.fulfilled, (state, action) => {
        state.companyFileData = state.companyFileData.filter(
          (file) => file.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteCompanyFile.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default companyFilesSlice.reducer;
